import { useCallback, useMemo } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FlagGroup, FlagLabel } from '@/models/template';
import flagsBarStyles from '@/features/report/Flags/FlagsBar.module.css';
import { useFlagClassOptions } from '../editorOptions';
import { ClassPicker } from '../primitives/ClassPicker';
import { RowControls } from '../primitives/RowControls';
import { humanizeClass } from '../primitives/format';
import styles from './EditableFlagsBar.module.css';

interface EditableFlagsBarProps {
  flagGroups: FlagGroup[];
  onChange: (groups: FlagGroup[]) => void;
}

/**
 * Editable version of FlagsBar. Renders all groups' flag chips inline.
 * Each group has its own sortable context — chips can be reordered
 * within their parent group.
 */
export function EditableFlagsBar({
  flagGroups,
  onChange,
}: EditableFlagsBarProps) {
  const flagClasses = useFlagClassOptions();

  const usedClasses = useMemo(() => {
    const used = new Set<string>();
    flagGroups.forEach((g) =>
      g.flagLabels.forEach((fl) => {
        if (fl.flagClass) used.add(fl.flagClass);
      })
    );
    return used;
  }, [flagGroups]);

  const handleFlagDelete = useCallback(
    (groupIdx: number, flagIdx: number) => {
      const groups = [...flagGroups];
      const group = { ...groups[groupIdx] };
      group.flagLabels = group.flagLabels.filter((_, i) => i !== flagIdx);
      groups[groupIdx] = group;
      onChange(groups);
    },
    [flagGroups, onChange]
  );

  const handleAddFlagWithClass = useCallback(
    (flagClass: string) => {
      const groups =
        flagGroups.length > 0
          ? [...flagGroups]
          : [
              {
                uuid: crypto.randomUUID(),
                label: 'Flags',
                index: 0,
                flagLabels: [] as FlagLabel[],
                style: '',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              } satisfies FlagGroup,
            ];
      const target = { ...groups[0] };
      const maxIndex = target.flagLabels.reduce(
        (m, f) => Math.max(m, f.index),
        -1
      );
      const now = new Date().toISOString();
      const newFlag: FlagLabel = {
        uuid: crypto.randomUUID(),
        label: humanizeClass(flagClass),
        flagClass,
        index: maxIndex + 1,
        createdAt: now,
        updatedAt: now,
      };
      target.flagLabels = [...target.flagLabels, newFlag];
      groups[0] = target;
      onChange(groups);
    },
    [flagGroups, onChange]
  );

  const handleReorder = useCallback(
    (groupIdx: number, activeId: string, overId: string) => {
      const groups = [...flagGroups];
      const group = { ...groups[groupIdx] };
      const flags = [...group.flagLabels];
      const oldIdx = flags.findIndex((f) => f.uuid === activeId);
      const newIdx = flags.findIndex((f) => f.uuid === overId);
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(flags, oldIdx, newIdx).map((f, i) => ({
        ...f,
        index: i,
      }));
      group.flagLabels = reordered;
      groups[groupIdx] = group;
      onChange(groups);
    },
    [flagGroups, onChange]
  );

  return (
    <div className={`${flagsBarStyles.bar} ${styles.editableBar}`}>
      {flagGroups.map((group, groupIdx) => (
        <FlagGroupSortable
          key={group.uuid}
          group={group}
          onFlagDelete={(flagIdx) => handleFlagDelete(groupIdx, flagIdx)}
          onReorder={(activeId, overId) =>
            handleReorder(groupIdx, activeId, overId)
          }
        />
      ))}
      <ClassPicker
        value=""
        options={flagClasses}
        usedClasses={usedClasses}
        onChange={(value) => value && handleAddFlagWithClass(value)}
        placeholder="+ Add flag"
        className={styles.addPicker}
      />
    </div>
  );
}

interface FlagGroupSortableProps {
  group: FlagGroup;
  onFlagDelete: (flagIdx: number) => void;
  onReorder: (activeId: string, overId: string) => void;
}

function FlagGroupSortable({
  group,
  onFlagDelete,
  onReorder,
}: FlagGroupSortableProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const flags = group.flagLabels.filter(
    (f) => !f.flagClass.includes('FOLLOW_UP')
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={flags.map((f) => f.uuid)}
        strategy={horizontalListSortingStrategy}
      >
        {flags.map((flag) => {
          const flagIdx = group.flagLabels.indexOf(flag);
          return (
            <SortableFlagChip
              key={flag.uuid}
              flag={flag}
              onDelete={() => onFlagDelete(flagIdx)}
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
}

interface SortableFlagChipProps {
  flag: FlagLabel;
  onDelete: () => void;
}

function SortableFlagChip({ flag, onDelete }: SortableFlagChipProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: flag.uuid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <span
      ref={setNodeRef}
      style={style}
      className={styles.chip}
      data-editable-row
      {...attributes}
      {...listeners}
    >
      <span>{flag.label || humanizeClass(flag.flagClass)}</span>
      <RowControls onDelete={onDelete} />
    </span>
  );
}
